import styled from "@emotion/styled"
import { PHeading } from "@porsche-design-system/components-react"
import {
	colorCanvas,
	radius3Xl,
	spacingStaticLg,
	spacingStaticMd,
	spacingStaticSm,
} from "@porsche-design-system/components-react/emotion"
import type { HTMLAttributes, PropsWithChildren, ReactNode, Ref } from "react"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { NewBadge } from "../badge/NewBadge"
import { ButtonGroup } from "../form/buttonGroup/ButtonGroup"

// inert attribute to disable everything inside stage, useful when out of viewport, e.g. for filters
// see https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/inert
export interface StageProps extends Pick<HTMLAttributes<HTMLElement>, "inert" | "tabIndex">, PropsWithChildren {
	actionButtons?: ReactNode
	/** applied on inner ContentWrapper, not root element */
	className?: string
	headerChildren?: ReactNode
	id?: string
	isNew?: boolean
	ref?: Ref<HTMLDivElement>
	title?: string
}

export const Stage = ({
	actionButtons,
	children,
	className,
	headerChildren,
	isNew,
	ref,
	title,
	...rest
}: StageProps) => (
	<Wrapper $hasActionButtons={!!actionButtons} {...rest} ref={ref}>
		{title && (
			<Header>
				<PHeading size="lg" tag="h2">
					{title}
					{isNew && <StyledNewBadge compact />}
				</PHeading>
				{headerChildren}
			</Header>
		)}
		{actionButtons && (
			<StyledButtonGroup direction="row" withoutMarginTop>
				{actionButtons}
			</StyledButtonGroup>
		)}
		{children ? <InnerContentWrapper className={className}>{children}</InnerContentWrapper> : null}
	</Wrapper>
)

const Wrapper = styled("section", { shouldForwardProp })<{ $hasActionButtons: boolean }>`
	display: grid;
	grid-template-columns: ${({ $hasActionButtons }) => ($hasActionButtons ? "1fr auto" : "1fr")};
	gap: ${spacingStaticMd};
	position: relative;
	background: ${colorCanvas};
	border-radius: ${radius3Xl};
	padding: ${spacingStaticLg};
`

const Header = styled.header`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: ${spacingStaticSm};
`

const StyledNewBadge = styled(NewBadge)`
	vertical-align: middle;
	margin-inline-start: ${spacingStaticSm};
	margin-top: -6px;
`

const StyledButtonGroup = styled(ButtonGroup)`
	align-self: start;
	grid-column: 2;
`

const InnerContentWrapper = styled.div`
	grid-column: 1 / -1;
`
